import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Eye, FileText, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

export default function QuotationListPage() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchQuotations();
  }, [statusFilter]);

  const fetchQuotations = async () => {
    try {
      const params = { limit: 50 };
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/quotations", { params });
      setQuotations(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { setQuotations([]); }
  };

  const statusColors = {
    DRAFT: "bg-gray-100 text-gray-700",
    PENDING_APPROVAL: "bg-yellow-100 text-yellow-800",
    APPROVED: "bg-green-100 text-green-800",
    FOR_REVISION: "bg-orange-100 text-orange-800",
    CANCELLED: "bg-red-100 text-red-800",
  };

  const filtered = quotations.filter((q) =>
    !searchTerm ||
    q.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="quotation-list-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]" style={{ fontFamily: "Chivo, sans-serif" }}>
            Quotations
          </h1>
          <p className="text-sm text-gray-500 mt-1">{total} total quotations</p>
        </div>
        <Button
          data-testid="new-quotation-button"
          onClick={() => navigate("/quotations/new")}
          className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm"
        >
          <Plus size={16} className="mr-1.5" /> New Quotation
        </Button>
      </div>

      <Card className="border border-gray-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <div className="flex gap-3 mb-4">
            <Input
              data-testid="quotation-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search quotations..."
              className="max-w-xs rounded-sm h-9 border-gray-300"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="quotation-status-filter" className="w-[180px] rounded-sm h-9">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="PENDING_APPROVAL">Pending Approval</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="FOR_REVISION">For Revision</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500 border-b border-gray-200">
                  <th className="text-left py-3 px-3">Number</th>
                  <th className="text-left py-3 px-3">Customer</th>
                  <th className="text-left py-3 px-3">Type</th>
                  <th className="text-left py-3 px-3">Status</th>
                  <th className="text-right py-3 px-3">Amount</th>
                  <th className="text-left py-3 px-3">Created</th>
                  <th className="text-right py-3 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-gray-400">No quotations found</td></tr>
                )}
                {filtered.map((q) => (
                  <tr
                    key={q.id}
                    data-testid={`quotation-row-${q.number}`}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/quotations/${q.id}`)}
                  >
                    <td className="py-3 px-3 font-medium text-[#0052CC]">{q.number}</td>
                    <td className="py-3 px-3 text-gray-700">{q.customer_name}</td>
                    <td className="py-3 px-3">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-sm bg-gray-100">{q.type}</span>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm ${statusColors[q.status] || ""}`}>
                        {q.status?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right font-medium">{q.total_amount?.toFixed(2)}</td>
                    <td className="py-3 px-3 text-gray-500 text-xs">
                      {q.created_at ? new Date(q.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-3 px-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                            <MoreHorizontal size={14} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/quotations/${q.id}`)}>
                            <Eye size={14} className="mr-2" /> View
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
